import React from 'react';
import PropTypes from 'prop-types';
import { colors } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';
import InnovationIllustration from 'svg/illustrations/Innovation';

const DEFAULT_BENEFIT_ITEMS = [
  {
    title: 'Infraestructura de Vanguardia (Innovación)',
    subtitle:
      'Ponemos a tu disposición tecnología de grado internacional. Nuestra ingeniería técnica es tu partner estratégico para que tu contenido compita al más alto nivel global.',
    colorKey: 'blue',
    iconType: 'innovation',
  },
  {
    title: 'Comunidad TNO Media Lab (Humano)',
    subtitle:
      'Al entrar al Lab, te unes a una red activa de colaboración. Fomentamos el intercambio de ideas entre podcasters, periodistas y especialistas. Aquí nacen las alianzas que transforman programas individuales en movimientos mediáticos.',
    colorKey: 'teal',
    iconType: 'community',
  },
  {
    title: 'Formación & Masterclasses (Crecimiento)',
    subtitle:
      'La innovación requiere actualización. Ofrecemos sesiones de formación en media training, narrativa digital, monetización y tendencias tecnológicas. En nuestra comunidad, el aprendizaje es constante para asegurar que siempre estés un paso adelante.',
    colorKey: 'amber',
    iconType: 'growth',
  },
  {
    title: 'TNO Digest: Tu Autoridad Digital',
    subtitle:
      'Como miembro del Lab, tienes acceso directo a nuestra unidad editorial. Convertimos tu conocimiento audiovisual en artículos de valor dentro de nuestro portal, construyendo tu estatus de experto y mejorando tu posicionamiento SEO en la web.',
    colorKey: 'pink',
    iconType: 'digest',
  },
];

const benefitIcons = {
  innovation: (
    <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  ),
  community: (
    <svg width={30} height={24} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M8.25 6.75a3.75 3.75 0 1 1 7.5 0 3.75 3.75 0 0 1-7.5 0ZM15.75 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM2.25 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM6.31 15.117A6.745 6.745 0 0 1 12 12a6.745 6.745 0 0 1 6.709 7.498.75.75 0 0 1-.372.568A12.696 12.696 0 0 1 12 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 0 1-.372-.568 6.787 6.787 0 0 1 1.019-4.38Z" clipRule="evenodd" />
      <path d="M5.082 14.254a8.287 8.287 0 0 0-1.308 5.135 9.687 9.687 0 0 1-1.764-.44l-.115-.04a.563.563 0 0 1-.373-.487l-.01-.121a3.75 3.75 0 0 1 3.57-4.047ZM20.226 19.389a8.287 8.287 0 0 0-1.308-5.135 3.75 3.75 0 0 1 3.57 4.047l-.01.121a.563.563 0 0 1-.373.486l-.115.04c-.567.2-1.156.349-1.764.441Z" />
    </svg>
  ),
  growth: (
    <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  digest: (
    <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
};

const Benefits = ({ themeMode = 'light', content = {} }) => {
  const benefitItems = (content.items && content.items.length > 0 ? content.items : DEFAULT_BENEFIT_ITEMS).map(
    (item, i) => ({
      ...DEFAULT_BENEFIT_ITEMS[i % DEFAULT_BENEFIT_ITEMS.length],
      ...item,
      icon: benefitIcons[item.iconType || DEFAULT_BENEFIT_ITEMS[i % DEFAULT_BENEFIT_ITEMS.length].iconType],
      color: colors[item.colorKey || DEFAULT_BENEFIT_ITEMS[i % DEFAULT_BENEFIT_ITEMS.length].colorKey],
    }),
  );

  return (
    <Box>
      <Box marginBottom={4}>
        <Typography
          sx={{ textTransform: 'uppercase', fontWeight: 'medium' }}
          gutterBottom
          color={'secondary'}
          align={'center'}
        >
          {content.overline || 'Diferenciadores'}
        </Typography>
        <Box
          component={Typography}
          sx={{ fontWeight: 700 }}
          variant={'h3'}
          gutterBottom
          align={'center'}
        >
          {content.title || 'Innovación + Comunidad'}
        </Box>
        <Typography align={'center'} color="textSecondary" variant={'h6'}>
          {content.subtitle ||
            'Tecnología de élite y comunidad de aprendizaje en TNO Media Lab: producción avanzada, networking entre talentos y formación continua.'}
        </Typography>
      </Box>
      <Box
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        marginBottom={4}
        data-aos={'fade-up'}
      >
        <Box maxWidth={300} width={'100%'}>
          <InnovationIllustration width={'100%'} height={'100%'} />
        </Box>
      </Box>
      <Grid container spacing={2}>
        {benefitItems.map((item, i) => (
          <Grid item xs={12} md={6} key={i}>
            <Box width={1} height={'100%'} data-aos={'fade-up'}>
              <Box
                display={'flex'}
                flexDirection={'column'}
                alignItems={'center'}
              >
                <Box
                  component={Avatar}
                  variant="rounded"
                  width={60}
                  height={60}
                  marginBottom={2}
                  bgcolor={item.color[themeMode === 'light' ? 50 : 100]}
                  color={item.color[themeMode === 'light' ? 500 : 700]}
                >
                  {item.icon}
                </Box>
                <Typography
                  variant={'h6'}
                  gutterBottom
                  sx={{ fontWeight: 500 }}
                  align={'center'}
                >
                  {item.title}
                </Typography>
                <Typography align={'center'} color="textSecondary">
                  {item.subtitle}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

Benefits.propTypes = {
  themeMode: PropTypes.string.isRequired,
  content: PropTypes.object,
};

export default Benefits;
